import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { QUESTIONNAIRE } from "../lib/questionnaire";
import { useAuth, formatError } from "../lib/auth";
import api from "../lib/api";
import { ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

export default function Questionnaire() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const section = QUESTIONNAIRE[step];
  const totalSteps = QUESTIONNAIRE.length;
  const progress = ((step) / totalSteps) * 100;

  const setAnswer = (qid, value) => {
    setAnswers((prev) => ({
      ...prev,
      [section.key]: { ...(prev[section.key] || {}), [qid]: value },
    }));
  };

  const sectionAnswered = section.questions.every((q) => answers[section.key]?.[q.id]);

  const next = async () => {
    if (step < totalSteps - 1) { setStep(step + 1); window.scrollTo(0, 0); }
    else {
      setSaving(true); setError("");
      try {
        await api.post("/profile/questionnaire", { answers });
        await refresh();
        nav("/home");
      } catch (e) { setError(formatError(e)); }
      finally { setSaving(false); }
    }
  };

  return (
    <div className="app-shell bg-aurora min-h-screen px-7 pt-10 pb-10" data-testid="questionnaire-page">
      {/* Progress bar */}
      <div className="flex items-center gap-3 mb-8">
        <button onClick={() => step > 0 && setStep(step - 1)} disabled={step === 0} className="text-[#8A7E91] disabled:opacity-30" data-testid="q-back">
          <ArrowLeft size={20} strokeWidth={1.7} />
        </button>
        <div className="flex-1 h-1.5 rounded-full bg-[#EFE0E5] overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#082A24] to-[#0F3D34] transition-all duration-500" style={{ width: `${progress + (100 / totalSteps)}%` }} />
        </div>
        <span className="text-xs text-[#8A7E91] tabular-nums" data-testid="q-step">{step + 1}/{totalSteps}</span>
      </div>

      <div className="flex items-center gap-3 mb-1">
        <span className="text-4xl">{section.icon}</span>
        <div>
          <p className="text-xs uppercase tracking-widest text-[#082A24] font-medium">
            {section.group === "Já" ? "✦ O tobě" : "✦ Hledáš u partnera"} · Sekce {step + 1}
          </p>
          <h1 className="font-display text-3xl text-[#332D38]">{section.title}</h1>
          {section.subtitle && <p className="text-sm text-[#8A7E91] mt-0.5">{section.subtitle}</p>}
        </div>
      </div>
      <p className="text-[#54635F] mb-8 mt-2 text-sm">AI použije tvé odpovědi k výpočtu kompatibility s ostatními. ✨</p>

      <div className="space-y-6">
        {section.questions.map((q) => (
          <div key={q.id} className="glass-card p-5" data-testid={`q-${section.key}-${q.id}`}>
            <p className="text-[#332D38] font-medium mb-3">{q.label}</p>
            <div className="flex flex-wrap gap-2">
              {q.options.map(([val, label]) => {
                const active = answers[section.key]?.[q.id] === val;
                return (
                  <button key={val}
                    type="button"
                    onClick={() => setAnswer(q.id, val)}
                    data-testid={`opt-${section.key}-${q.id}-${val}`}
                    className={`px-4 py-2 rounded-full text-sm transition-all ${active ? "bg-gradient-to-r from-[#082A24] to-[#0F3D34] text-white shadow-md" : "bg-white border border-[#DEE7E2] text-[#332D38]"}`}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-2xl p-3 mt-6">{error}</div>}

      <button onClick={next} disabled={!sectionAnswered || saving} className="btn-primary mt-8 flex items-center justify-center gap-2" data-testid="q-next">
        {saving ? "Ukládám..." : step === totalSteps - 1 ? (<>Dokončit <Sparkles size={18} /></>) : (<>Pokračovat <ArrowRight size={18} /></>)}
      </button>
    </div>
  );
}
